/**
 * Unified Data Helper Functions
 * Provides helper functions to access consolidated data from the unified business structure
 * VERBESSERT: Verwendet zentralen localStorageManager
 */

// Import centralized Business ID function
import {
  getCurrentBusinessId,
  businessDataStorageKey,
  normalizeBusinessStorageId,
  readBusinessMergedPayloadRaw,
  readBusinessMergedPayloadParsed
} from './sharedCore';
import { queueBusinessPayloadWrite } from '../infrastructure/storage/businessPayloadWrite';
import localStorageManager from '../services/localStorageManager';
import { isRestaurantBusinessType } from './restaurantMode';
import { ensureRestaurantStockLocations } from '../domain/restaurant/restaurantStockLocations';

// Re-export for backward compatibility
export {
  getCurrentBusinessId,
  businessDataStorageKey,
  normalizeBusinessStorageId,
  readBusinessMergedPayloadRaw,
  readBusinessMergedPayloadParsed
};

/**
 * Get business data from localStorage (zentralisiert)
 */
export const getBusinessData = () => {
  return localStorageManager.getBusinessData();
};

/**
 * Save business data to localStorage with Auto-Save (zentralisiert)
 */
export const saveBusinessData = async (key, value, options = {}) => {
  return localStorageManager.setBusinessData(key, value, null, options);
};

/**
 * Get stock locations from unified structure
 */
export const getStockLocations = () => {
  try {
    const businessData = getBusinessData();
    
    if (!businessData) {
      return ['Principal'];
    }
    
    // Get predefined locations from business data
    const predefinedLocations = businessData.stockLocations || ['Principal'];
    
    // Extract unique locations from lots with robust error handling
    let lotLocations = [];
    try {
      const lots = businessData.lots || [];
      
      // Handle both array and object formats
      let lotsArray = [];
      if (Array.isArray(lots)) {
        lotsArray = lots;
      } else if (lots && typeof lots === 'object') {
        lotsArray = Object.values(lots);
      } else {
        lotsArray = [];
      }
      
      lotLocations = [...new Set(lotsArray.map(lot => {
        if (lot && typeof lot === 'object' && lot.location) {
          return lot.location;
        }
        return null;
      }).filter(Boolean))];
    } catch (lotsError) {
      // Error processing lots
      lotLocations = [];
    }
    
    // Add common locations that might exist
    const commonLocations = ['Principal'];
    
    // Combine predefined, lot-based, and common locations
    let allLocations = [...new Set([...predefinedLocations, ...lotLocations, ...commonLocations])];

    // Restaurant: always surface Bar / Cuisine / Grill (even before first save).
    const bizType =
      businessData.businessInfo?.business_type ||
      businessData.metadata?.business_type ||
      null;
    if (isRestaurantBusinessType(bizType)) {
      allLocations = ensureRestaurantStockLocations(allLocations, bizType);
    }

    return allLocations;
  } catch (error) {
    // Error getting stock locations
    return ['Principal'];
  }
};

/**
 * Save stock locations to unified structure
 */
export const saveStockLocations = async (locations) => {
  return saveBusinessData('stockLocations', locations);
};

/**
 * Get persistent stock data from unified structure
 */
export const getPersistentStockData = () => {
  try {
    const businessData = getBusinessData();
    if (!businessData) return [];
    
    return businessData.persistentStockData || [];
  } catch (error) {
    // Error getting persistent stock data
    return [];
  }
};

/**
 * Save persistent stock data to unified structure
 */
export const savePersistentStockData = (data) => {
  return saveBusinessData('persistentStockData', data);
};

/**
 * Get offline queue from unified structure
 */
export const getOfflineQueue = () => {
  try {
    const businessData = getBusinessData();
    if (!businessData) return [];
    
    return businessData.offlineQueue || [];
  } catch (error) {
    // Error getting offline queue
    return [];
  }
};

/**
 * Save offline queue to unified structure
 */
export const saveOfflineQueue = (queue) => {
  return saveBusinessData('offlineQueue', queue);
};

/**
 * Get business info from unified structure
 */
export const getBusinessInfo = () => {
  try {
    const businessData = getBusinessData();
    if (!businessData) return null;
    
    return businessData.businessInfo || null;
  } catch (error) {
    // Error getting business info
    return null;
  }
};

/**
 * Save business info to unified structure
 */
export const saveBusinessInfo = (info) => {
  return saveBusinessData('businessInfo', info);
};

/**
 * Get user info from unified structure
 */
export const getUserInfo = () => {
  try {
    const businessData = getBusinessData();
    if (!businessData) return null;
    
    return businessData.userInfo || null;
  } catch (error) {
    // Error getting user info
    return null;
  }
};

/**
 * Save user info to unified structure
 */
export const saveUserInfo = (info) => {
  return saveBusinessData('userInfo', info);
};

/**
 * Get metadata from unified structure
 */
export const getMetadata = () => {
  try {
    const businessData = getBusinessData();
    if (!businessData) return {};
    
    return businessData.metadata || {};
  } catch (error) {
    // Error getting metadata
    return {};
  }
};

/**
 * Save metadata to unified structure
 */
export const saveMetadata = (metadata) => {
  return saveBusinessData('metadata', metadata);
};

/**
 * Update metadata field
 */
export const updateMetadataField = (field, value) => {
  try {
    const currentMetadata = getMetadata();
    const updatedMetadata = { ...currentMetadata, [field]: value };
    return saveMetadata(updatedMetadata);
  } catch (error) {
    // Error updating metadata field
    return false;
  }
};

/**
 * Get stock data for a specific product and location
 */
export const getStockDataForProduct = (productId, location) => {
  try {
    const businessData = getBusinessData();
    if (!businessData) return null;
    
    const stockKey = `${productId}_${location}`;
    return businessData.stockData?.[stockKey] || null;
  } catch (error) {
    // Error getting stock data for product
    return null;
  }
};

/**
 * Save stock data for a specific product and location
 */
export const saveStockDataForProduct = (productId, location, stockData) => {
  try {
    const businessId = getCurrentBusinessId();
    if (!businessId) return false;

    const stockKey = `${productId}_${location}`;
    const entry = {
      ...stockData,
      productId,
      location,
      lastUpdated: new Date().toISOString()
    };

    void queueBusinessPayloadWrite(
      businessId,
      (parsed) => {
        if (!parsed.stockData || typeof parsed.stockData !== 'object') {
          parsed.stockData = {};
        }
        parsed.stockData[stockKey] = entry;
        return parsed;
      },
      { authoritativeFields: ['stockData'] }
    ).catch((err) => {
      console.warn('[unifiedDataHelpers] saveStockDataForProduct failed:', err?.message || err);
    });

    return true;
  } catch (error) {
    return false;
  }
};
